/**
 * MfaGate (AUTH-MFA-01) — sits inside ProtectedRoute. Three outcomes:
 *   1. the account has an authenticator and this session has not answered a
 *      code yet → the code screen, nothing else renders until it passes;
 *   2. the org requires two-factor for admins, this user is an admin and has
 *      no authenticator yet → the enrollment screen (cannot be skipped);
 *   3. otherwise → the app.
 * Sign out is always available from these screens.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson } from "@/lib/orgSettings";
import { useMfaStatus, verifyCode, useInvalidateMfa } from "./mfa";
import { TotpEnrollPanel } from "./TotpEnrollPanel";

function Shell({ title, lede, children }: { title: string; lede: string; children: ReactNode }) {
  const { signOut, user } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex flex-col items-center gap-2">
          <img src="/pegasus-mark.png" alt="Pegasus" className="h-12 w-auto object-contain" />
          <h1 className="text-2xl font-light tracking-[0.2em] text-slate-800">PEGASUS</h1>
        </div>
        <div className="rounded-xl border border-hairline bg-white p-6 shadow-e2">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-[13.5px] text-slate-500">{lede}</p>
          {children}
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">
          Signed in as {user?.email} ·{" "}
          <button type="button" onClick={() => void signOut()} className="font-medium text-brand-700 hover:underline">Sign out</button>
        </p>
      </div>
    </div>
  );
}

function ChallengeScreen({ factorId }: { factorId: string }) {
  const invalidate = useInvalidateMfa();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyCode(factorId, code);
      invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }
  return (
    <Shell title="Enter your sign-in code" lede="Open your authenticator app and type the 6-digit code for Pegasus.">
      <form onSubmit={submit} className="mt-4 flex flex-wrap items-center gap-2">
        <input
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123 456"
          className="w-36 rounded-md border border-hairline bg-white px-3 py-2 font-mono text-lg tracking-[0.25em] text-slate-900 focus:border-brand-400 focus:outline-none"
        />
        <button type="submit" disabled={busy || code.replace(/\s/g, "").length !== 6}
          className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue
        </button>
      </form>
      {error && <p className="mt-3 text-xs text-red-700">{error}</p>}
      <p className="mt-4 text-xs text-slate-400">Lost your phone? An admin can remove the authenticator from your account in Supabase (Authentication → Users), then sign in again.</p>
    </Shell>
  );
}

function EnrollRequiredScreen() {
  return (
    <Shell title="Two-factor sign-in is required for admins" lede="Add an authenticator app once; after that, sign-in asks for a 6-digit code alongside your password.">
      <TotpEnrollPanel />
    </Shell>
  );
}

export function MfaGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const perms = usePermissions();
  const status = useMfaStatus(!!user);
  const requireAdmins = useOrgSettingJson<boolean>("mfa_required_admins", false).data ?? false;

  if (status.isLoading || !status.data) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
      </div>
    );
  }
  const s = status.data;
  if (s.needsChallenge && s.factorId) return <ChallengeScreen factorId={s.factorId} />;
  if (!s.enrolled && requireAdmins && perms.ready && perms.isAdmin) return <EnrollRequiredScreen />;
  return <>{children}</>;
}
